import { AppVersionActionTypes } from './constants';
import { AppVersionActionType } from './actions';
import { IAppVersion } from 'pages/apps/AppVersions/types';

export interface AppVersionState {
    appVersions: IAppVersion[];
    appVersion: IAppVersion | null;
    loading: boolean;
    error: string | null;
    isSuccess: boolean; // true after create / update / delete
}

const INIT_STATE: AppVersionState = {
    appVersions: [],
    appVersion: null,
    loading: false,
    error: null,
    isSuccess: false,
};

const AppVersionReducer = (
    state: AppVersionState = INIT_STATE,
    action: AppVersionActionType
): AppVersionState => {
    switch (action.type) {
        // ─── API_RESPONSE_SUCCESS ────────────────────────────────────────────────
        case AppVersionActionTypes.API_RESPONSE_SUCCESS:
            switch ((action.payload as any).actionType) {
                case AppVersionActionTypes.GET_APP_VERSIONS: {
                    const data = (action.payload as any).data;
                    return {
                        ...state,
                        appVersions: Array.isArray(data) ? data : data?.results || [],
                        loading: false,
                        error: null,
                        isSuccess: false,
                    };
                }

                case AppVersionActionTypes.GET_APP_VERSION_BY_ID: {
                    return {
                        ...state,
                        appVersion: (action.payload as any).data,
                        loading: false,
                        error: null,
                        isSuccess: false,
                    };
                }

                case AppVersionActionTypes.CREATE_APP_VERSION: {
                    const created: IAppVersion = (action.payload as any).data;
                    // Upsert: replace if same OS already exists, else prepend
                    const existing = state.appVersions.filter((v) => v.os !== created.os);
                    return {
                        ...state,
                        appVersions: [created, ...existing],
                        appVersion: created,
                        loading: false,
                        error: null,
                        isSuccess: true,
                    };
                }

                case AppVersionActionTypes.UPDATE_APP_VERSION: {
                    const updated: IAppVersion = (action.payload as any).data;
                    return {
                        ...state,
                        appVersions: state.appVersions.map((v) =>
                            v.id === updated.id ? updated : v
                        ),
                        appVersion: updated,
                        loading: false,
                        error: null,
                        isSuccess: true,
                    };
                }

                case AppVersionActionTypes.DELETE_APP_VERSION: {
                    const deletedId = (action.payload as any).data;
                    return {
                        ...state,
                        appVersions: state.appVersions.filter((v) => v.id !== deletedId),
                        loading: false,
                        error: null,
                        isSuccess: true,
                    };
                }

                default:
                    return state;
            }

        // ─── API_RESPONSE_ERROR ──────────────────────────────────────────────────
        case AppVersionActionTypes.API_RESPONSE_ERROR:
            switch ((action.payload as any).actionType) {
                case AppVersionActionTypes.GET_APP_VERSIONS:
                case AppVersionActionTypes.GET_APP_VERSION_BY_ID:
                case AppVersionActionTypes.CREATE_APP_VERSION:
                case AppVersionActionTypes.UPDATE_APP_VERSION:
                case AppVersionActionTypes.DELETE_APP_VERSION: {
                    return {
                        ...state,
                        loading: false,
                        error: (action.payload as any).error,
                        isSuccess: false,
                    };
                }

                default:
                    return state;
            }

        // ─── Loading triggers ────────────────────────────────────────────────────
        case AppVersionActionTypes.GET_APP_VERSIONS:
        case AppVersionActionTypes.GET_APP_VERSION_BY_ID:
        case AppVersionActionTypes.CREATE_APP_VERSION:
        case AppVersionActionTypes.UPDATE_APP_VERSION:
        case AppVersionActionTypes.DELETE_APP_VERSION: {
            return {
                ...state,
                loading: true,
                error: null,
                isSuccess: false,
            };
        }

        // ─── Reset ───────────────────────────────────────────────────────────────
        case AppVersionActionTypes.RESET: {
            return { ...INIT_STATE };
        }

        default:
            return state;
    }
};

export default AppVersionReducer;
